"use client";

/**
 * Which UPI surfaces a device can actually honour.
 *
 * ## Why this file exists
 *
 * The previous picker offered one row called "UPI app / QR" and let Razorpay
 * decide what that meant at tap time. On desktop it meant a QR; on mobile it
 * meant a list of the top PSP apps shown, in Razorpay's own words,
 * "irrespective of the installation status of the UPI apps". A customer without
 * PhonePe tapped the PhonePe tile and got "Unable to open UPI app" inside a
 * modal we had configured to contain nothing else. The row had promised two
 * things and delivered whichever one the device didn't need.
 *
 * So the branch moves here, before the customer chooses, and each row in the
 * sheet gets to be a single honest promise about what the next tap does.
 *
 * ## Why there is no app-installed check
 *
 * Because there cannot be a reliable one from a browser, and chasing it is what
 * produced the dead end. Android already has the answer: handing the OS a bare
 * `upi://pay?...` opens *its* chooser, populated with the UPI apps that are
 * genuinely installed. We stop guessing and let the OS answer.
 *
 * iOS has no such generic handler — which is precisely why NPCI exempted "iOS
 * mobile app and mobile web transactions" from the UPI Collect sunset. There,
 * the QR and a typed UPI ID are the honest options, and Razorpay's own Intent
 * screen (which can at least try per-app URL schemes) stays available rather
 * than being the only thing on offer.
 */
export type UpiSurface = {
  /**
   * A bare `upi://pay?...` will reach an OS app chooser listing installed apps.
   * Android only — and the reason the QR drawer can lead with a button there.
   */
  canOpenUpiApp: boolean;
  /** No camera pointing at this screen: the QR needs a second device or an app's gallery scan. */
  isDesktop: boolean;
  platform: "android" | "ios" | "desktop";
};

const readUserAgent = () => {
  if (typeof navigator === "undefined") return "";
  return navigator.userAgent ?? "";
};

/**
 * iPadOS 13+ reports itself as a Mac. `maxTouchPoints` is the standard tell,
 * and getting it wrong here would offer a touch device the desktop copy
 * ("scan with your phone") while it is holding the phone.
 */
const isIpadMasqueradingAsMac = () =>
  typeof navigator !== "undefined" &&
  /Macintosh/i.test(readUserAgent()) &&
  typeof navigator.maxTouchPoints === "number" &&
  navigator.maxTouchPoints > 1;

export const detectUpiSurface = (): UpiSurface => {
  const userAgent = readUserAgent();

  if (/Android/i.test(userAgent)) {
    return { canOpenUpiApp: true, isDesktop: false, platform: "android" };
  }

  if (/iPhone|iPad|iPod/i.test(userAgent) || isIpadMasqueradingAsMac()) {
    return { canOpenUpiApp: false, isDesktop: false, platform: "ios" };
  }

  return { canOpenUpiApp: false, isDesktop: true, platform: "desktop" };
};

/**
 * Whether Hiive may hand a payment to one named UPI app itself.
 *
 * Both mobile platforms, because the iOS failure this was briefly blamed on is
 * not a platform limit — it is a misread of the SDK, and reading the SDK's own
 * source (`checkout.razorpay.com/v1/razorpay.js`, 2026-09-04) settles it:
 *
 * - `customUpiIntentProcessing` refuses **locally**, before any network call,
 *   if the shortcode is missing from its static list: `setTimeout(() =>
 *   complete(getNoIntentAppError()), 100)`. `phonepe` and `gpay` are both in
 *   the iOS list, so that is not what our payers hit.
 * - On iOS (`An = /iPhone|iPad/`) the deep link is fired as a **top-level GET
 *   navigation** to `phonepe://…`, not `window.open`. Safari answers an
 *   unhandled scheme with its own native "the address is invalid" — an alert no
 *   JavaScript can see, catch or dismiss.
 * - Then it registers `is()`, which attaches a **one-shot `touchstart`** to
 *   `body`: on the payer's next touch, if the document never went hidden
 *   (`rs`), it calls `complete(getNoIntentAppError())`.
 *
 * So on iOS `intent_no_apps_error` does not mean "app not installed". It means
 * **"you touched the page again without ever leaving it"** — which is equally
 * true of a missing app, a declined "Open in PhonePe?" prompt, and a scheme
 * Safari refused for want of user activation. It can never be persisted as a
 * device fact, and it must never be the end of the flow.
 *
 * What it needs instead is a screen the payer lands on while the handoff is in
 * flight, with an **Open App** button that re-fires the deep link inside a
 * fresh gesture — the one thing Safari reliably honours, and the thing an
 * automatic attempt fired after two awaited round trips has already lost. See
 * `UpiIntentHandoff` in use-razorpay-custom-payment.
 */
export const supportsDirectUpiAppSelection = (surface: UpiSurface) =>
  surface.platform === "android" || surface.platform === "ios";

/**
 * The subtitle under "Scan QR code", written before the drawer opens.
 *
 * The whole point of splitting QR into its own row is that the customer knows
 * what Pay Now will do, so this says it plainly per device rather than
 * describing every branch to everyone.
 */
export const describeQrOption = (surface: UpiSurface) => {
  if (surface.isDesktop) {
    return "We'll show a QR to scan with any UPI app";
  }
  if (surface.canOpenUpiApp) {
    return "Open any UPI app, or scan from another phone";
  }
  return "We'll show a QR to scan from another phone";
};

/**
 * Is the provider-managed intent row the one to put "Recommended" on?
 *
 * Everywhere the handoff is arbitrated by something that knows what is
 * installed: Android's intent resolver, or the Capacitor shell's
 * `webview_intent`, which is the supported native path and works on device.
 *
 * Not on iOS mobile web. There the row leads to Razorpay's screen, whose tiles
 * come from the same static user-agent list ours did, and a tile for an app
 * this iPhone does not have ends at Safari's "the address is invalid" — an
 * alert no JavaScript can see, let alone recover from. The route stays (it is
 * the right one for the payer who does have the app) but it must not be the
 * one we push, because a typed UPI ID and a card cannot fail that way.
 */
export const recommendsProviderIntent = (
  surface: UpiSurface,
  nativeShell: boolean,
) => nativeShell || surface.platform !== "ios";

export const describeIntentOption = (
  surface: UpiSurface,
  nativeShell = false,
) => {
  if (surface.isDesktop) {
    return "Best on a phone — use the QR here instead";
  }
  /**
   * A promise about the menu, not about the device.
   *
   * The row opens a list of apps to pick from, so naming the big ones is simply
   * describing what is behind it. What it must never imply is that we checked —
   * nobody can, and an earlier version's "installed on this phone" claimed
   * exactly the knowledge whose absence produces "Unable to open UPI app". If
   * the pick turns out to be wrong the drawer says so and keeps going, which is
   * where that guarantee actually lives.
   */
  if (recommendsProviderIntent(surface, nativeShell)) {
    return "Google Pay, PhonePe, Paytm and more";
  }
  /**
   * iOS mobile web states the condition instead of naming the apps.
   *
   * This is the one surface where a wrong pick is not recoverable inside the
   * page — Safari's own error alert is what the payer meets — so the row says
   * what the tap needs before it happens rather than apologising after. Still
   * no claim of knowledge: a condition ("if you have it") is not the assertion
   * ("installed on this phone") that made the old dead end feel like a lie.
   */
  return "Opens the app you pick, if you have it on this device";
};
